import { useCallback, useState, type FormEvent } from 'react';
import { FilterX } from 'lucide-react';
import { MetricCard, PageHeader } from '../components/ui';
import { ReadState } from '../components/ReadState';
import { RequestTable } from '../components/RequestTable';
import { useConsole } from '../state/ConsoleProvider';
import type { UsageFilters } from '../services/console';
import { useReadResource, usd } from '../lib/live';

const PAGE_SIZE = 20;
type FilterDraft = { start: string; end: string; model: string; key: string };

function initialDraft(): FilterDraft {
  const today = new Date().toISOString().slice(0, 10);
  return { start: `${today.slice(0, 8)}01`, end: today, model: '', key: '' };
}

function timestamp(date: string, end = false): number | undefined {
  if (!date) return undefined;
  const milliseconds = Date.parse(`${date}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(milliseconds) || new Date(milliseconds).toISOString().slice(0, 10) !== date || milliseconds <= 0) throw new Error('Enter valid dates after January 1, 1970.');
  return milliseconds / 1000 + (end ? 86_399 : 0);
}

function toFilters(draft: FilterDraft): UsageFilters {
  const start = timestamp(draft.start);
  const end = timestamp(draft.end, true);
  if (start !== undefined && end !== undefined && start > end) throw new Error('The end date must be on or after the start date.');
  const model = draft.model.trim();
  const key = draft.key.trim();
  if ([model, key].some(value => value.length > 200 || /[\u0000-\u001f\u007f]/.test(value))) throw new Error('Keep model and key names under 200 characters and remove control characters.');
  return { ...(start === undefined ? {} : { start }), ...(end === undefined ? {} : { end }), ...(model ? { model } : {}), ...(key ? { key } : {}) };
}

export default function Usage() {
  const { api } = useConsole();
  const [draft, setDraft] = useState(initialDraft);
  const [filters, setFilters] = useState<UsageFilters>(() => toFilters(initialDraft()));
  const [filterError, setFilterError] = useState('');
  const [page, setPage] = useState(1);
  const loadSummary = useCallback(() => api.usageSummary(filters), [api, filters]);
  const loadHistory = useCallback(() => api.usageHistory(filters, page, PAGE_SIZE), [api, filters, page]);
  const summary = useReadResource(loadSummary, 'Your usage summary could not be loaded. Try again.');
  const history = useReadResource(loadHistory, 'Your usage history could not be loaded. Try again.');

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try { const next = toFilters(draft); setFilters(next); setPage(1); setFilterError(''); }
    catch (cause) { setFilterError(cause instanceof Error ? cause.message : 'Check your filters.'); }
  }

  function resetFilters() {
    const next = initialDraft();
    setDraft(next);
    setFilters(toFilters(next));
    setPage(1);
    setFilterError('');
  }

  return <>
    <PageHeader eyebrow="CONSUMPTION & COST" title="Usage" description="Review your Roten API consumption and available costs." />
    <form className="live-filter-form" onSubmit={applyFilters}>
      <div className="filter-row live-filter-row">
        <label className="filter-control"><span>Start date (UTC)</span><input className="input" type="date" min="1970-01-02" value={draft.start} onChange={event => setDraft({ ...draft, start: event.target.value })} /></label>
        <label className="filter-control"><span>End date (UTC)</span><input className="input" type="date" min={draft.start || '1970-01-02'} value={draft.end} onChange={event => setDraft({ ...draft, end: event.target.value })} /></label>
        <label className="filter-control"><span>Model name</span><input className="input" placeholder="All models" maxLength={200} value={draft.model} onChange={event => setDraft({ ...draft, model: event.target.value })} /></label>
        <label className="filter-control"><span>API key name</span><input className="input" placeholder="All API keys" maxLength={200} value={draft.key} onChange={event => setDraft({ ...draft, key: event.target.value })} /></label>
        <button className="button button-primary" type="submit">Apply filters</button>
        <button className="button button-ghost" type="button" onClick={resetFilters}><FilterX size={15} />Reset</button>
      </div>
      <p className="live-filter-hint">Dates use UTC. The end date includes the full day through 23:59:59.</p>
      {filterError && <p className="management-form-error" role="alert">{filterError}</p>}
    </form>

    <section aria-label="Usage summary" className="live-summary-section">
      <ReadState {...summary} label="Loading usage summary…" />
      {summary.data && <div className="metric-grid live-summary-grid">
        <MetricCard label="Calculated usage cost" value={<span className="live-money">{usd(summary.data.amountUsd)}</span>} detail={summary.data.amountUsd === null ? 'A USD total is not available for these filters.' : 'USD · before wallet settlement rounding'} />
        <MetricCard label="Requests per minute" value={summary.data.rpm.toLocaleString('en-US')} />
        <MetricCard label="Tokens per minute" value={summary.data.tpm.toLocaleString('en-US')} />
      </div>}
    </section>

    <p className="live-filter-hint">Calculated costs are reported by your billing account. Wallet settlement can round each request, so these amounts can differ from wallet deductions. Settled units are shown when available; historical wallet debits in USD are not provided by the billing account.</p>

    <section className="panel" aria-labelledby="history-heading">
      <div className="panel-header"><div><h2 id="history-heading">Consumption history</h2><p>Request details for the selected filters.</p></div>{history.data && <span className="muted small-text">{history.data.total.toLocaleString('en-US')} records</span>}</div>
      <ReadState {...history} label="Loading usage history…" />
      {history.data && <>
        <RequestTable records={history.data.items} />
        <div className="pagination"><span>{history.data.items.length ? `Showing ${(history.data.page - 1) * history.data.pageSize + 1}–${(history.data.page - 1) * history.data.pageSize + history.data.items.length} of ${history.data.total.toLocaleString('en-US')}` : `${history.data.total.toLocaleString('en-US')} records`} · Page {history.data.page}</span><div><button className="button button-secondary" disabled={history.data.page <= 1} onClick={() => setPage(Math.max(1, history.data!.page - 1))}>Previous</button><button className="button button-secondary" disabled={history.data.page * history.data.pageSize >= history.data.total} onClick={() => setPage(history.data!.page + 1)}>Next</button></div></div>
      </>}
    </section>
  </>;
}
